import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Crown, CalendarClock, CheckCircle2, AlertTriangle, RefreshCw,
  Receipt, ShieldCheck, XCircle, Sparkles, BadgeIndianRupee,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MODE_LABEL = {
  auto: "Auto-renewing subscription",
  one_time: "One-time payment",
  dev: "Preview access",
  admin: "Admin access",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Account() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [refundReason, setRefundReason] = useState(null);
  const [refundNote, setRefundNote] = useState("");

  const REFUND_REASONS = [
    "Too expensive",
    "Not enough time to learn",
    "Content didn't fit my level",
    "I can find videos myself",
    "Technical issues",
    "Other",
  ];

  const load = async () => {
    try {
      const { data } = await api.get("/payments/me");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load your subscription.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data: r } = await api.post("/payments/cancel");
      toast.success(r.message || "Auto-renew turned off.");
      setConfirmCancel(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not cancel.");
    } finally {
      setCancelling(false);
    }
  };

  const doRefund = async () => {
    setRefunding(true);
    try {
      const { data: r } = await api.post("/payments/refund", {
        reason: refundReason,
        note: refundNote.trim() || null,
      });
      toast.success(r.message || "Refund issued.");
      setConfirmRefund(false);
      await checkAuth();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not process the refund.");
    } finally {
      setRefunding(false);
    }
  };

  const active = data?.active;
  const expired = data?.status === "expired";
  const none = data?.status === "none";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="account-page">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary">
            <Crown className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-heading font-black tracking-tight text-2xl sm:text-3xl">My subscription</h1>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center" data-testid="account-loading">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="mt-6 space-y-6"
          >
            {/* ===== Status card ===== */}
            <div className="rounded-3xl border border-border bg-card p-5 sm:p-7 shadow-sm" data-testid="account-status-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span
                  data-testid="account-status-badge"
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold ${
                    active ? "bg-success/10 text-success"
                      : expired ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {active ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  {active ? "Active" : expired ? "Expired" : "No active plan"}
                </span>
                <span className="text-sm font-semibold text-muted-foreground" data-testid="account-mode">
                  {MODE_LABEL[data?.mode] || "Free"}
                </span>
              </div>

              {!none && (
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  <Detail label="Plan" value={data?.plan_label || "—"} icon={Sparkles} />
                  <Detail
                    label={data?.mode === "auto" ? "Renews" : "Access until"}
                    value={data?.mode === "auto" && active && data?.auto_renew
                      ? "Renews automatically"
                      : (data?.current_end ? fmtDate(data.current_end) : "—")}
                    icon={CalendarClock}
                  />
                  {data?.mode === "one_time" && active && data?.days_left != null && (
                    <Detail label="Days remaining" value={`${data.days_left} day${data.days_left === 1 ? "" : "s"}`} icon={CalendarClock} highlight />
                  )}
                  <Detail label="Active since" value={fmtDate(data?.activated_at)} icon={ShieldCheck} />
                </div>
              )}

              {data?.cancel_requested && active && (
                <p className="mt-4 text-xs font-medium text-amber-700 dark:text-amber-400 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Auto-renew is off — your access stays until the current period ends.
                </p>
              )}

              {/* actions */}
              <div className="mt-6 flex flex-wrap gap-3">
                {(none || expired || data?.mode === "one_time") && (
                  <Button
                    data-testid="account-renew-btn"
                    onClick={() => navigate("/subscribe?renew=1")}
                    className="rounded-xl bg-primary text-primary-foreground font-bold"
                  >
                    <RefreshCw className="h-4 w-4" /> {expired || none ? "Get Pro again" : "Renew / extend"}
                  </Button>
                )}
                {data?.mode === "auto" && active && data?.auto_renew && (
                  <Button
                    data-testid="account-cancel-btn"
                    variant="outline"
                    onClick={() => setConfirmCancel(true)}
                    className="rounded-xl"
                  >
                    <XCircle className="h-4 w-4" /> Cancel auto-renew
                  </Button>
                )}
              </div>
            </div>

            {/* ===== 7-day money-back guarantee ===== */}
            {data?.refund?.eligible && (
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-500/5 dark:border-emerald-500/20 p-5 sm:p-7" data-testid="refund-card">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-heading font-extrabold tracking-tight text-lg">7-day money-back guarantee</h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Not loving it? You're within your guarantee window
                  {data.refund.days_left != null && <> (<b>{data.refund.days_left} day{data.refund.days_left === 1 ? "" : "s"} left</b>)</>}.
                  One click and we'll refund your full <b>₹{data.refund.amount?.toLocaleString("en-IN")}</b> — no questions asked.
                </p>
                <Button
                  data-testid="refund-btn"
                  variant="outline"
                  onClick={() => setConfirmRefund(true)}
                  className="mt-4 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400"
                >
                  <BadgeIndianRupee className="h-4 w-4" /> Get a full refund
                </Button>
              </div>
            )}

            {/* ===== Payment history ===== */}
            <div className="rounded-3xl border border-border bg-card p-5 sm:p-7 shadow-sm" data-testid="account-history">
              <div className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                <h2 className="font-heading font-extrabold tracking-tight text-lg">Payment history</h2>
              </div>
              {data?.history?.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                        <th className="py-2 pr-3 font-semibold">Date</th>
                        <th className="py-2 pr-3 font-semibold">Plan</th>
                        <th className="py-2 pr-3 font-semibold">Amount</th>
                        <th className="py-2 font-semibold">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.history.map((p, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0" data-testid="account-history-row">
                          <td className="py-2.5 pr-3">{fmtDate(p.created_at)}</td>
                          <td className="py-2.5 pr-3 capitalize">{p.plan || "—"}</td>
                          <td className="py-2.5 pr-3 font-semibold">₹{p.amount ?? "—"}</td>
                          <td className="py-2.5 text-muted-foreground">
                            {p.status === "refunded"
                              ? <span className="inline-flex items-center gap-1 text-xs font-bold text-destructive">Refunded</span>
                              : (p.source === "one_time" ? "One-time" : p.source === "webhook" ? "Auto-renew" : (p.source || "—"))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground" data-testid="account-no-history">
                  No payments yet.{data?.mode === "admin" ? " (Admin access doesn't require payment.)" : ""}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </main>

      {/* refund confirm */}
      {confirmRefund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm px-4" onClick={() => setConfirmRefund(false)} data-testid="refund-confirm-modal">
          <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-heading font-extrabold tracking-tight text-lg">Refund ₹{data?.refund?.amount?.toLocaleString("en-IN")}?</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              We'll refund the full amount to your original payment method (arrives in 5–7 business days) and turn off
              any auto-renewal. Pro access ends immediately, but your path and progress stay saved.
            </p>
            <p className="mt-4 text-xs font-bold text-foreground">Before you go — what made you leave? <span className="font-normal text-muted-foreground">(optional)</span></p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="refund-reason-chips">
              {REFUND_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`refund-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setRefundReason(refundReason === r ? null : r)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${refundReason === r
                    ? "border-emerald-500 bg-emerald-600 text-white"
                    : "border-border bg-background text-muted-foreground hover:border-emerald-400 hover:text-foreground"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            {refundReason === "Other" && (
              <textarea
                data-testid="refund-note-input"
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value.slice(0, 500))}
                placeholder="Tell us a little more (optional)…"
                rows={2}
                className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none"
              />
            )}
            <div className="mt-5 flex gap-2.5">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setConfirmRefund(false)} data-testid="refund-keep-btn">Keep Pro</Button>
              <Button className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={doRefund} disabled={refunding} data-testid="refund-confirm-btn">
                {refunding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, refund me"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* cancel confirm */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm px-4" onClick={() => setConfirmCancel(false)} data-testid="cancel-confirm-modal">
          <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-heading font-extrabold tracking-tight text-lg">Cancel auto-renew?</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              You'll keep full access until the end of your current billing period — we just won't charge you again.
            </p>
            <div className="mt-5 flex gap-2.5">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setConfirmCancel(false)} data-testid="cancel-keep-btn">Keep it</Button>
              <Button className="flex-1 rounded-xl bg-destructive text-destructive-foreground font-bold" onClick={doCancel} disabled={cancelling} data-testid="cancel-confirm-btn">
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, cancel"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, icon: Icon, highlight }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`grid place-items-center h-9 w-9 shrink-0 rounded-xl ${highlight ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
        <p className={`font-heading font-bold ${highlight ? "text-primary" : ""}`}>{value}</p>
      </div>
    </div>
  );
}
